import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { accountCodeService } from "services";
import {
  AccountCodeForm,
  AccountCodeQuery,
  AccountCodeUpdatePayload,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type AccountCodeListKey =
  | ["account-codes"]
  | ["account-codes", AccountCodeQuery]
  | ["account-codes", "type", string]
  | ["account-codes", "type", string, AccountCodeQuery];
type AccountCodeDetailKey =
  | ["account-codes", "detail", number | string]
  | ["account-codes", "detail", string, number | string];

const accountCodeKeys = {
  all: (type?: string) =>
    type ? (["account-codes", "type", type] as const) : (["account-codes"] as const),
  list: (params?: AccountCodeQuery, type?: string): AccountCodeListKey => {
    if (type && params) return ["account-codes", "type", type, params];
    if (type) return ["account-codes", "type", type];
    return params ? ["account-codes", params] : ["account-codes"];
  },
  detail: (id: number | string, type?: string): AccountCodeDetailKey =>
    type
      ? ["account-codes", "detail", type, id]
      : ["account-codes", "detail", id],
};

export function useAccountCode(type?: string) {
  const queryClient = useQueryClient();

  const getTypedParams = (params?: AccountCodeQuery) =>
    type ? { ...params, type } : params;

  const fetchList = (params?: AccountCodeQuery, enabled = true) => {
    const queryParams = getTypedParams(params);
    return useQuery({
      queryKey: accountCodeKeys.list(queryParams, type),
      queryFn: () => accountCodeService.list(queryParams),
      enabled,
      // staleTime: 1000 * 60 * 3,
      select: (data) => ({
        data: data?.data ?? [],
        totalPage: Math.max(
          1,
          data?.totalPage ??
            data?.pagination?.totalPage ??
            data?.pagination?.total ??
            1,
        ),
      }),
    });
  };

  const fetchDetail = (id: number | string, enabled = true) => {
    return useQuery({
      queryKey: accountCodeKeys.detail(id, type),
      queryFn: () => accountCodeService.show(id, type),
      enabled: Boolean(id) && enabled,
      // staleTime: 1000 * 60 * 3,
    });
  };

  const createAccountCode = useMutation({
    mutationFn: (payload: AccountCodeForm) =>
      accountCodeService.store(
        type ? { ...payload, type } : payload,
        type,
      ),
    onSuccess: (res) => {
      if (res?.statusCode === 200 || res?.statusCode === 201) {
        SnackbarUtils.success(res?.message ?? "Tạo mã tài khoản thành công");
      }
      queryClient.invalidateQueries({ queryKey: accountCodeKeys.all(type) });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Tạo mã tài khoản thất bại");
      console.error("createAccountCode failed", err);
    },
  });

  const updateAccountCode = useMutation({
    mutationFn: (payload: AccountCodeUpdatePayload) =>
      accountCodeService.update(
        type ? { ...payload, type } : payload,
        type,
      ),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Cập nhật mã tài khoản thành công");
      }
      queryClient.invalidateQueries({ queryKey: accountCodeKeys.all(type) });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Cập nhật mã tài khoản thất bại");
      console.error("updateAccountCode failed", err);
    },
  });

  const deleteAccountCode = useMutation({
    mutationFn: (id: number | string) => accountCodeService.destroy(id, type),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Xoá mã tài khoản thành công");
      }
      queryClient.invalidateQueries({ queryKey: accountCodeKeys.all(type) });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Xoá mã tài khoản thất bại");
      console.error("deleteAccountCode failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createAccountCode,
    updateAccountCode,
    deleteAccountCode,
  };
}
